import React, { useEffect, useState, useRef, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { CodeIcon, PaletteIcon, CameraIcon, ServerIcon, CpuIcon, LineChartIcon, ArrowRightIcon, UsersIcon, ZapIcon, ShieldCheckIcon } from 'lucide-react';
import { AnimatedSection, AnimatedChildren, AnimatedChild } from '../components/ui/AnimatedSection';
import { VideoHero } from '../components/ui/VideoHero';
import { TextReveal } from '../components/ui/TextReveal';
import { ParallaxImage } from '../components/ui/ParallaxImage';
import { MaskReveal } from '../components/ui/MaskReveal';
import { Parallax3D } from '../components/ui/Parallax3D';
const stats = [{
  value: 150,
  suffix: '+',
  label: 'Projekte'
}, {
  value: 12,
  suffix: '+',
  label: 'Jahre Erfahrung'
}, {
  value: 98,
  suffix: '%',
  label: 'Kundenzufriedenheit'
}, {
  value: 45,
  suffix: '+',
  label: 'Unternehmenskunden'
}];
const services = [{
  icon: CodeIcon,
  title: 'Digital Engineering',
  description: 'Individualsoftware, Webplattformen, Schnittstellen & Automatisierung',
  href: '/leistungen/digital-engineering'
}, {
  icon: PaletteIcon,
  title: 'Corporate Design & Marke',
  description: 'Markenstrategie, visuelle Identität & Designsysteme',
  href: '/leistungen/corporate-design'
}, {
  icon: CameraIcon,
  title: 'Foto & Video',
  description: 'Imagefilme, Produktfotografie & Contentproduktion',
  href: '/leistungen/foto-video'
}, {
  icon: ServerIcon,
  title: 'Technische Lösungen',
  description: 'Systemintegration, Cloud & Performance',
  href: '/leistungen/technische-loesungen'
}, {
  icon: CpuIcon,
  title: 'KI & Automation',
  description: 'Prozessautomatisierung & KI-gestützte Workflows',
  href: '/leistungen/ki-automation'
}, {
  icon: LineChartIcon,
  title: 'Digitale Beratung',
  description: 'Transformationsberatung & Digitalstrategie',
  href: '/leistungen'
}];
const projects = [{
  title: 'Digitale Plattform für Logistikunternehmen',
  industry: 'Logistik & Transport',
  result: '40% Effizienzsteigerung in der Auftragsabwicklung',
  image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&q=80',
  codeSnippet: 'await deploy(config)'
}, {
  title: 'Markenrelaunch für Maschinenbauer',
  industry: 'Industrie & Fertigung',
  result: 'Erfolgreiche Neupositionierung im Premium-Segment',
  image: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800&q=80',
  codeSnippet: 'const brand = redesign()'
}, {
  title: 'Imagefilm für Technologiekonzern',
  industry: 'Technologie',
  result: '2,5 Mio. Views auf Corporate Channels',
  image: 'https://images.unsplash.com/photo-1535016120720-40c646be5580?w=800&q=80',
  codeSnippet: 'render(<Film />)'
}];
const whyUs = [{
  icon: ZapIcon,
  title: 'Strategie & Umsetzung aus einer Hand',
  description: 'Von der ersten Idee bis zum fertigen Produkt – wir begleiten Sie durch den gesamten Prozess ohne Schnittstellenverluste.'
}, {
  icon: CodeIcon,
  title: 'Technisches Know-how & Kreativität',
  description: 'Unsere Teams vereinen technische Exzellenz mit kreativem Denken für Lösungen, die funktionieren und begeistern.'
}, {
  icon: UsersIcon,
  title: 'Skalierbare Lösungen für Wachstum',
  description: 'Wir entwickeln Systeme, die mit Ihrem Unternehmen wachsen und sich flexibel an neue Anforderungen anpassen.'
}, {
  icon: ShieldCheckIcon,
  title: 'Zuverlässiger Partner auf Augenhöhe',
  description: 'Transparente Kommunikation, verbindliche Zusagen und langfristige Partnerschaften sind unser Fundament.'
}];
function AnimatedCounter({
  value,
  suffix
}: {
  value: number;
  suffix: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  return <span ref={ref} className="gradient-text" data-id="element-384">
      {count}
      {suffix}
    </span>;
}
export function HomePage() {
  return <div className="w-full" data-id="element-385">
      <VideoHero data-id="element-386" />

      {/* Wrapper with overflow clip for all content below hero —
            this prevents horizontal scroll without breaking sticky above */}
      <div className="overflow-x-hidden" data-id="element-387">
        {/* Stats Section */}
        <section className="py-16 md:py-20 border-b border-white/10 bg-[#09090B]" data-id="element-388">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-389">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12" data-id="element-390">
              {stats.map((stat, index) => <AnimatedSection key={stat.label} delay={index * 0.1} data-id="element-391">
                  <div className="text-center" data-id="element-392">
                    <div className="text-4xl md:text-5xl font-bold tracking-tight" data-id="element-393">
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} data-id="element-394" />
                    </div>
                    <div className="mt-2 text-sm text-white/40" data-id="element-395">
                      {stat.label}
                    </div>
                  </div>
                </AnimatedSection>)}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-24 md:py-32 bg-[#09090B]" data-id="element-396">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-397">
            <AnimatedSection data-id="element-398">
              <div className="max-w-2xl" data-id="element-399">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest gradient-text" data-id="element-400">
                  Was wir leisten
                </h2>
                <p className="mt-4 text-lg text-white/60" data-id="element-401">
                  Full-Service-Lösungen für Ihre digitale Transformation – von
                  der Strategie bis zur Umsetzung.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedChildren className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1} data-id="element-402">
              {services.map(service => <AnimatedChild key={service.title} data-id="element-403">
                  <Parallax3D intensity={0.5} tiltX={4} shiftY={20} scaleRange={[0.98, 1.02]} data-id="element-404">
                    <Link to={service.href} className="group block p-8 bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl hover:border-[#E8781A]/30 hover:shadow-lg hover:shadow-[#E8781A]/10 hover:bg-white/[0.08] transition-all duration-300 h-full" data-id="element-405">
                      <service.icon className="w-8 h-8 text-[#E8781A]" data-id="element-406" />
                      <h3 className="mt-6 text-xl font-semibold text-white" data-id="element-407">
                        {service.title}
                      </h3>
                      <p className="mt-3 text-white/50 leading-relaxed" data-id="element-408">
                        {service.description}
                      </p>
                      <div className="mt-6 flex items-center text-sm font-medium text-white/60 group-hover:text-[#E8781A] transition-colors" data-id="element-409">
                        Mehr erfahren
                        <ArrowRightIcon className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-410" />
                      </div>
                    </Link>
                  </Parallax3D>
                </AnimatedChild>)}
            </AnimatedChildren>
          </div>
        </section>

        {/* Projects Section */}
        <section className="py-24 md:py-32 bg-[#0C0C0F50]" data-id="element-411">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-412">
            <AnimatedSection data-id="element-413">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-414">
                <div data-id="element-415">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest gradient-text" data-id="element-416">
                    Ausgewählte Projekte
                  </h2>
                  <p className="mt-4 text-lg text-white/50 max-w-xl" data-id="element-417">
                    Einblicke in erfolgreiche Zusammenarbeit mit unseren Kunden.
                  </p>
                </div>
                <Link to="/projekte" className="inline-flex items-center text-sm font-medium text-white/60 hover:text-[#E8781A] transition-colors" data-id="element-418">
                  Alle Projekte
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-419" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedChildren className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8" staggerDelay={0.15} data-id="element-420">
              {projects.map(project => <AnimatedChild key={project.title} data-id="element-421">
                  <Link to="/projekte" className="group block" data-id="element-422">
                    <MaskReveal direction="up" perspective={true} className="mb-6" data-id="element-423">
                      <Parallax3D intensity={0.8} tiltX={5} shiftY={30} className="aspect-[4/3]" data-id="element-424">
                        <div className="relative w-full h-full overflow-hidden rounded-2xl" data-id="element-425">
                          <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-426" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100" data-id="element-427">
                            <div className="font-mono text-xs text-[#E8781A]/80 bg-black/50 px-4 py-2 rounded-lg" data-id="element-428">
                              {project.codeSnippet}
                            </div>
                          </div>
                        </div>
                      </Parallax3D>
                    </MaskReveal>
                    <span className="text-xs font-medium uppercase tracking-wider text-white/40" data-id="element-429">
                      {project.industry}
                    </span>
                    <h3 className="mt-2 text-xl font-semibold text-white group-hover:text-[#E8781A] transition-colors" data-id="element-430">
                      {project.title}
                    </h3>
                    <p className="mt-2 text-white/50" data-id="element-431">{project.result}</p>
                  </Link>
                </AnimatedChild>)}
            </AnimatedChildren>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="py-24 md:py-32 bg-[#09090B]" data-id="element-432">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-433">
            <AnimatedSection data-id="element-434">
              <div className="text-center max-w-2xl mx-auto" data-id="element-435">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest gradient-text" data-id="element-436">
                  Warum fooble media
                </h2>
                <p className="mt-4 text-lg text-white/50" data-id="element-437">
                  Was uns als Partner für Ihr Unternehmen auszeichnet.
                </p>
              </div>
            </AnimatedSection>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-438">
              {whyUs.map((item, index) => <AnimatedSection key={item.title} delay={index * 0.1} variant={index % 2 === 0 ? 'slideLeft' : 'slideRight'} data-id="element-439">
                  <div className="flex gap-6" data-id="element-440">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center" data-id="element-441">
                      <item.icon className="w-6 h-6 text-[#E8781A]" data-id="element-442" />
                    </div>
                    <div data-id="element-443">
                      <h3 className="text-xl font-semibold text-white" data-id="element-444">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-white/50 leading-relaxed" data-id="element-445">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </AnimatedSection>)}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 md:py-32 bg-gradient-to-br from-[#E8781A]/10 via-[#09090B] to-[#a855f7]/10 border-y border-white/5" data-id="element-446">
          <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-447">
            <AnimatedSection data-id="element-448">
              <div className="max-w-3xl mx-auto text-center" data-id="element-449">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tightest text-white" data-id="element-450">
                  Lassen Sie uns über Ihr Projekt sprechen.
                </h2>
                <p className="mt-6 text-lg text-white/50" data-id="element-451">
                  In einem unverbindlichen Erstgespräch lernen wir Ihre
                  Herausforderungen kennen und zeigen Ihnen, wie wir Sie
                  unterstützen können.
                </p>
                <div className="mt-10" data-id="element-452">
                  <Link to="/kontakt" className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#09090B] font-medium rounded-lg hover:bg-white/90 transition-colors" data-id="element-453">
                    Termin vereinbaren
                    <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-454" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </div>;
}